import TooltipLink from "@/components/TooltipLink/TooltipLink";
import useTranslation from "@/hooks/useTranslation";
import { TravelMode, useDeleteTravelMode } from "@/pages/employee/travel_modes/useTravelMode";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { message, Popconfirm, Tooltip } from "antd";
import { ReactNode } from "react";

export default function DeleteTravelModeAction({
  children,
  travelModes,
  onSuccess,
  deselectUnDeletableTravelModes,
}: {
  children: ReactNode;
  travelModes?: TravelMode[];
  onSuccess?: () => void;
  deselectUnDeletableTravelModes?: () => void;
}) {
  const deleteTravelModes = useDeleteTravelMode();
  const travelModeLength = travelModes ? travelModes.length : 0;

  const productText =
    travelModeLength === 1
      ? "travel mode"
      : `${travelModeLength} travel mode${travelModeLength > 1 && "s"}`;

  const disabled = travelModes?.some((x) => x.relations > 0);
  const undeletableCount = travelModes?.filter((x) => x.relations > 0).length;
  const translation = useTranslation();

  return (
    <Popconfirm
      title={`Delete ${productText}`}
      description={
        <>
          <p>Deleting a travel mode also deletes:</p>
          <ul>
            <li>matches to the impact reference</li>
          </ul>
          <p style={{ color: "#F5222D" }}>This action can not be undone.</p>
        </>
      }
      onConfirm={() => {
        if (travelModes) {
          deleteTravelModes
            .mutateAsync({ travelModeIds: travelModes.map((x) => x.id) })
            .then((result) => {
              if (result.status === 200) {
                message.success(`Travel mode${travelModes.length > 1 ? "s" : ""} removed.`);
                onSuccess?.();
              }
            })
            .catch(() => message.error(translation.errors.general));
        }
      }}
      onCancel={() => {}}
      icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
      okText={`Delete ${productText}`}
      cancelText="Cancel"
      placement="bottom"
      okType="danger"
      okButtonProps={{ loading: deleteTravelModes.isLoading }}
      disabled={disabled}
    >
      <Tooltip
        placement="bottom"
        title={
          disabled &&
          (travelModeLength === 1 ? (
            <p>
              This travel mode can not be deleted as it is referenced in a employee commute and/or
              business travel. Please delete the associated entry first before deleting this travel
              mode.
            </p>
          ) : (
            <p>
              {undeletableCount} of the travel modes you selected are referenced in a employee
              commute and/or business travel. Edit or remove the relations to delete the travel
              modes from the database.{" "}
              {deselectUnDeletableTravelModes ? (
                <>
                  <TooltipLink onClick={() => deselectUnDeletableTravelModes()}>
                    Click here
                  </TooltipLink>{" "}
                  to deselect them or remove the related employee commute and/or business travel to
                  continue.
                </>
              ) : (
                <>Delete the related employee commute and/or business travel to continue.</>
              )}
            </p>
          ))
        }
      >
        {children}
      </Tooltip>
    </Popconfirm>
  );
}
